import _ from 'lodash'
import type { CalcViewerModelFunctionLibrary, ComponentsRegistryFunctionLibrary } from 'thunderbolt-viewer-model-root'
import { getCSRFToken, parseCookieString, stringifyThemeOverrides } from '@wix/thunderbolt-commons'
import { buildOgImageFullUrl } from '@wix/advanced-seo-utils/renderer'
// TODO resolve cyclic deps, find a way to move this code to thunderbolt-platform
import {
	getPlatformSiteConfig,
	getWixCodeBundlersUrlData,
	getClientSpecMapWithWixCodeApps,
	getWixCodeAppDataKey,
} from './features/platform'
import { createViewerAppsUrlMap } from './features/platformUrlTemplating'
import { mapLanguageCodeToName, mapLanguageCodeToLocalizedName, utils } from '@wix/santa-multilingual/dist/languages' // eslint-disable-line no-restricted-syntax
import { CommonConfig, PageAccessTopology, SiteModel } from '@wix/thunderbolt-ssr-api'
import { SiteScopeParams } from '@wix/thunderbolt-symbols'
import ooiCssFunctionLibrary from './ooiCssFunctionLibrary'
import { getComponentsLibraries } from '@wix/thunderbolt-components-registry'

const getQueryParamValue = (
	requestUrl: string,
	paramName: string,
	options: { toLowerCase?: boolean } = { toLowerCase: true }
): string | null => {
	const _url = options.toLowerCase ? requestUrl.toLowerCase() : requestUrl
	const _param = options.toLowerCase ? paramName.toLowerCase() : paramName
	const { searchParams } = new URL(_url)
	return searchParams.get(_param)
}

const parseUrl = (_url: string) => {
	return new URL(_url)
}

const setProtocol = (_url: string, protocol: string) => _url.replace(/https?:/, protocol)

const getLangFromHost = (url: string): string | null => {
	const urlObj = new URL(url)
	const host = urlObj.host
	return /^[a-z][a-z]\./.test(host) ? host.substring(0, 2) : null
}

const getLangFromCookie = (cookieString: string): string => {
	const parsedCookie = parseCookieString(cookieString)
	return parsedCookie.wixLanguage
}

const getExperimentsObject = (experiments: string, defaultValue: string) =>
	experiments
		.split(',')
		.filter((x) => x)
		.reduce((acc, x) => {
			const [name, value = defaultValue] = x.split(':')
			return { ...acc, [name]: value }
		}, {})

const getExperimentsFromUrl = (absoluteUrl: string) => {
	const parsedUrl = new URL(absoluteUrl)

	const experiments = parsedUrl.searchParams.get('experiments') || ''
	const experimentsOff =
		parsedUrl.searchParams.get('experimentsOff') || parsedUrl.searchParams.get('experimentsoff') || ''

	const open = getExperimentsObject(experiments, 'new')
	const closed = getExperimentsObject(experimentsOff, 'old')

	return {
		...open,
		...closed,
	}
}

const parseCookie = (cookie = '') => {
	const cookies = cookie.split(/;\s*/)

	return cookies.reduce((acc: Record<string, string>, str: string) => {
		const [key, value] = str.split('=')
		acc[key] = value
		return acc
	}, {})
}

const parseJSON = (val: string): any => {
	try {
		return val && JSON.parse(val)
	} catch (e) {
		return null
	}
}

const stringifyQuery = (queryObj: { [key: string]: string }) =>
	Object.keys(queryObj)
		.map((key) => `${key}=${encodeURIComponent(queryObj[key])}`)
		.join('&')

const stringifyJSON = (json: any) => json && JSON.stringify(json)

const base64Encode = (value: string) => Buffer.from(value).toString('base64')

const regExpExec = (str: string, pattern: string) => {
	const [match] = new RegExp(pattern).exec(str) || []
	return match
}

const dropTrailingSlash = (str: string) => (str.endsWith('/') ? str.substr(0, str.length - 1) : str)

const includes = (str: string, searchString: string) => str.includes(searchString)

const getPageJsonServerUrls = (topology: Array<PageAccessTopology>): Array<string> => {
	return _.map(topology, (topologyItem) => {
		// same special case as here: https://github.com/wix-private/build-pipeline-poc/commit/34c05889fa2db205a5f4966d5f8b130d01848fd3#diff-17874c28c3f99772139a112769d10883e53c08ea2485258845177f810f046723R10
		if (_.includes(topologyItem.parts, 'wix-html-editor-pages-webapp')) {
			return `${dropTrailingSlash(topologyItem.baseUrl)}/wix-html-editor-pages-webapp/page`
		}

		return dropTrailingSlash(topologyItem.baseUrl)
	})
}

const parseCommaSeparatedObject = (str: string = '') =>
	_(str)
		.split(',')
		.compact()
		.map((e) => {
			// split k=v pair, take into account = sign can be present in the value as well
			const [, k, v] = /([^=]+)=(.+)/.exec(e) || []
			return [k || e, v]
		})
		.keyBy((e) => e[0])
		.mapValues((v) => v[1])
		.value()

const extractSiteAssetsParams = (str: string) =>
	_.chain(str)
		.split('?')
		.last()
		.split('&')
		.map((x) => x.replace('}', '').replace('{', '').split('='))
		.fromPairs()
		.value()

const extractDisabledComponents = (str?: string) =>
	_(str || '')
		.split(',')
		.compact()
		.keyBy((e) => e)
		.mapValues(() => true)
		.value()

const getSiteRevisionConfig = (
	commonConfigFromPublicModel: CommonConfig = {}
): SiteScopeParams['siteRevisionConfig'] => {
	const { siteRevision, branchId } = commonConfigFromPublicModel
	if (siteRevision && branchId) {
		return {
			siteRevision,
			branchId,
		}
	}
	return {}
}

export function getRegistryComponentsLibraries({
	serviceTopology,
	originalRequest,
	useNewStatics,
	experimentalMobileLibrary,
	experimentalMobileLibraryReactNative,
}: {
	serviceTopology: SiteModel['serviceTopology']
	originalRequest: SiteModel['originalRequest']
	useNewStatics?: boolean
	experimentalMobileLibrary?: boolean
	experimentalMobileLibraryReactNative?: boolean
}) {
	return getComponentsLibraries({
		useNewStatics,
		serviceTopology,
		url: originalRequest.url,
		experimentalMobileLibrary,
		experimentalMobileLibraryReactNative,
	})
}

const componentsRegistryFunctionLibrary: ComponentsRegistryFunctionLibrary = {
	getRegistryComponentsLibraries,
}

export const viewerModelFunctionLibrary: CalcViewerModelFunctionLibrary = {
	replace: (value, str, replacement) => value && value.replace(str, replacement),
	capitalize: (str: string) => _.capitalize(str),
	getQueryParamValue,
	parseUrl,
	parseCookie,
	getCSRFToken,
	parseJSON,
	getLangFromHost,
	getLangFromCookie,
	buildOgImageFullUrl,
	setProtocol,
	stringifyQuery,
	stringifyJSON,
	getExperimentsFromUrl,
	getPlatformSiteConfig,
	getClientSpecMapWithWixCodeApps,
	getWixCodeAppDataKey,
	mapLanguageCodeToName,
	mapLanguageCodeToLocalizedName,
	getFlagIconUrl: utils.getFlagIconUrl,
	base64Encode,
	regExpExec,
	createViewerAppsUrlMap,
	getWixCodeBundlersUrlData,
	dropTrailingSlash,
	includes,
	getPageJsonServerUrls,
	getSiteRevisionConfig,
	extractSiteAssetsParams,
	parseCommaSeparatedObject,
	stringifyThemeOverrides,
	extractDisabledComponents,
	...ooiCssFunctionLibrary,
	...componentsRegistryFunctionLibrary,
}
